import React, { useEffect, useRef, useState } from "react";
import { getWithAuth } from "../../../api/api";
import { API_URLS } from "../../../api/apiConfig";
import { useAdminRolesListContext } from "../adminRolesListContext";
import AutoDismissAlert from "../../../components/autoDismiss";
import ModalRolePermissions from "../components/ModalRolePermissions";

const AdminRolesListPage = () => {
  const rolesCtx = useAdminRolesListContext();
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const resp = await getWithAuth(API_URLS.GET_ROLES);
      if (resp.message === "success") {
        setRoles(resp.data || []);
      } else {
        rolesCtx.setMessage(resp.detail || "Error al obtener roles");
        rolesCtx.setAlertType("danger");
        rolesCtx.openAlertHeader();
      }
    } catch (e) {
      console.error("Error fetching roles:", e);
      rolesCtx.setMessage("Ha ocurrido un error conectando con el servidor");
      rolesCtx.setAlertType("danger");
      rolesCtx.openAlertHeader();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar">
          <div
            id="kt_app_toolbar_container"
            className="app-container container-fluid d-flex flex-stack"
          >
            <div className="page-title d-flex flex-column justify-content-center me-3 mb-6 mb-lg-0">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center me-3 mb-3 my-0">
                Admin - Roles (solo lectura)
              </h1>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div
            id="kt_app_content_container"
            className="app-container container-fluid"
          >
            {rolesCtx.showAlertHeader && (
              <div className="">
                <AutoDismissAlert
                  message={rolesCtx.message}
                  alertType={rolesCtx.alertType}
                  onClose={rolesCtx.closeAlertHeader}
                />
              </div>
            )}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "50vh" }}
              >
                <div
                  className="spinner-grow text-color-verde-wofyrooms"
                  style={{ width: "5rem", height: "5em" }}
                  role="status"
                />
              </div>
            ) : (
              <div className="card mb-5 mb-xxl-10">
                <div className="card-header border-0 pt-6">
                  <div className="card-title">
                    <h3>Listado de roles</h3>
                  </div>
                </div>
                <div className="card-body py-10">
                  <div
                    id="kt_table_roles_wrapper"
                    className="dataTables_wrapper dt-bootstrap4 no-footer"
                  >
                    <div className="table-responsive">
                      <table
                        className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer"
                        id="kt_table_roles"
                      >
                        <thead>
                          <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                            <th className="min-w-125px">Nombre</th>
                            <th className="min-w-200px">Descripción</th>
                            <th className="min-w-125px">Módulos con acceso</th>
                            <th className="min-w-125px">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {roles.map((role) => {
                            const perms = role.permissions || {};
                            const accessCount = Object.values(perms).filter(
                              (p) => p?.can_access_module
                            ).length;
                            return (
                              <tr key={role.id}>
                                <td>{role.name}</td>
                                <td>{role.description || "—"}</td>
                                <td>
                                  <span className="badge badge-light-primary">
                                    {accessCount} módulos
                                  </span>
                                </td>
                                <td>
                                  <TableActions role={role} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* si necesitas paginación, colócala aquí */}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {rolesCtx.showRolePermissionsModal && (
        <ModalRolePermissions
          onClose={rolesCtx.closeRolePermissionsModal}
          role={rolesCtx.selectedRole}
        />
      )}
    </>
  );
};

// dentro de AdminRolesListPage.jsx
const TableActions = ({ role }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);
  const anchorRef = useRef(null);
  const rolesCtx = useAdminRolesListContext();

  const toggleDropdown = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (showMenu && anchorRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const elementWidth = anchorRef.current.offsetWidth;
      const screenWidth = window.innerWidth;
      const menuX = anchorRect.left - screenWidth + elementWidth;
      const menuY = anchorRect.top + anchorRect.height + 10;
      setMenuPosition({ x: menuX, y: menuY });
    }
  }, [showMenu]);

  return (
    <>
      <a
        className="btn btn-sm btn-light btn-flex btn-center btn-active-light-primary show menu-dropdown"
        onClick={toggleDropdown}
        ref={anchorRef}
      >
        Acciones
        <i className="ki-outline ki-down text-color-azul-wofyrooms fs-5 ms-1"></i>
      </a>
      <div
        ref={menuRef}
        style={{
          transform: `translate3d(${menuPosition.x}px, ${menuPosition.y}px, 0)`,
        }}
        className={`menu-actions-table menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-125px py-4 ${
          showMenu ? "show" : ""
        }`}
      >
        <div className="menu-item px-3">
          <a
            className="menu-link px-3"
            onClick={() => {
              rolesCtx.openRolePermissionsModal(role);
              setShowMenu(false);
            }}
          >
            Ver permisos
          </a>
        </div>
      </div>
    </>
  );
};

export default AdminRolesListPage;
